import t from "@/lang/lang";
import { TextInput } from "@/shared/components/form/text-input";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { CommonDialogFooter } from "@/shared/components/dialog/dialog-common";
import { Edit } from "lucide-react";
import { useEffect, useState } from "react";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { TextareaInput } from "@/shared/components/form/textarea-input";
import { Textblock } from "../types/textblock";
import {
  UpdateRequest,
  useTextblocksTextblockUpdateMutation,
} from "../api/textblock-api";

export function EditTextblockDialog({ textblock }: { textblock: Textblock }) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<UpdateRequest>({
    ...textblock,
  });
  const [patch, { isLoading, error, isSuccess }] =
    useTextblocksTextblockUpdateMutation();

  useEffect(() => {
    if (isSuccess) {
      setOpen(false);
      setRequest({ ...textblock });
    }
  }, [textblock, isSuccess]);

  const disabled = textblock.immutable;

  const doUpdate = () => {
    if (isLoading || disabled) return;

    patch(request);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild className="ml-auto" disabled={disabled}>
        <Button size="sm" variant="outline">
          <Edit />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Bemerkung bearbeiten")}</DialogTitle>
          <DialogDescription>
            {t("Bearbeiten Sie diese Bemerkung.")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <TextInput
            label={t("Kürzel (optional)")}
            field="shorthand"
            text={request.shorthand}
            onTextChange={(shorthand) => setRequest({ ...request, shorthand })}
            errors={inputValidationErrorMessages(error, "shorthand")}
          />
          <TextInput
            label={t("Titel (optional)")}
            field="title"
            text={request.title}
            onTextChange={(title) => setRequest({ ...request, title })}
            errors={inputValidationErrorMessages(error, "title")}
          />
          <TextareaInput
            label={t("Text")}
            field="text"
            text={request.text}
            onTextChange={(text) => setRequest({ ...request, text })}
            errors={inputValidationErrorMessages(error, "title")}
          />
        </div>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter isLoading={false} onClickAction={doUpdate} />
      </DialogContent>
    </Dialog>
  );
}
